import { Button, Css, useSuperDrawer } from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import { SaveItemInput } from "src/generated/graphqlTypes";
import { useSaveItemMutation } from "src/routes/admin/items/ItemsPage.generated";
import {
  useMaterialLeadTimesEditorTabMetadataQuery,
  useMaterialLeadTimesEditorTabQuery,
} from "src/routes/admin/items/MaterialLeadTimesEditorTab.generated";
import { BoundMaterialLeadTimeConfigFields } from "../components/BoundMaterialLeadTimeConfigFields";
import { BoundMaterialLeadTimeConfigVariantsField } from "../components/BoundMaterialLeadTimeConfigVariantsField";
import {
  MaterialLeadTimeFormValue,
  initializeMaterialLeadTimeConfigFields,
  materialLeadTimeConfigConfig,
  maybeMaterialLeadTimeConfig,
} from "../utils";

type MaterialLeadTimesEditorTabProps = { itemId: string };

export function MaterialLeadTimesEditorTab({ itemId }: MaterialLeadTimesEditorTabProps) {
  const isFormNew = itemId === undefined;
  const { data } = useMaterialLeadTimesEditorTabQuery({ variables: { itemId: itemId! }, skip: isFormNew });
  const { data: marketData } = useMaterialLeadTimesEditorTabMetadataQuery();
  const [item] = data?.items ?? [];
  const { closeDrawer } = useSuperDrawer();
  const [saveItem] = useSaveItemMutation();

  const formState = useFormState({
    config: formConfig,
    init: {
      input: item,
      map: (item) => ({
        materialLeadTimeConfig: { ...initializeMaterialLeadTimeConfigFields(item.materialLeadTimeConfig) },
      }),
    },
  });

  const onSave = useCallback(async () => {
    if (formState.canSave()) {
      const { materialLeadTimeConfig } = formState.value;
      await saveItem({
        variables: {
          input: {
            id: item?.id,
            ...maybeMaterialLeadTimeConfig({ id: item?.materialLeadTimeConfig?.id, ...materialLeadTimeConfig }),
          },
        },
      });
      closeDrawer();
    }
  }, [item, formState, saveItem, closeDrawer]);

  const onDelete = useCallback(async () => {
    // we can only delete a "config" if it actually exists
    if (item?.materialLeadTimeConfig?.id) {
      await saveItem({
        variables: {
          input: { id: item.id, materialLeadTimeConfig: { delete: true, id: item.materialLeadTimeConfig.id } },
        },
      });
    }
  }, [saveItem, item]);
  return (
    <>
      {/* Fields for MaterialLeadTimeConfig */}
      <BoundMaterialLeadTimeConfigFields
        minLeadTimeInDays={formState.materialLeadTimeConfig.minLeadTimeInDays}
        expectedLeadTimeInDays={formState.materialLeadTimeConfig.expectedLeadTimeInDays}
        maxLeadTimeInDays={formState.materialLeadTimeConfig.maxLeadTimeInDays}
        reasonNote={formState.materialLeadTimeConfig.reasonNote}
        deleteConfig={{ canDelete: !!item?.materialLeadTimeConfig?.id, onDelete }}
      />
      {/* Fields for MaterialLeadTimeConfigVariant */}
      <BoundMaterialLeadTimeConfigVariantsField
        materialLeadTimeConfig={formState.materialLeadTimeConfig}
        markets={marketData?.markets ?? []}
      />
      <div css={Css.df.jcfe.gap1.$}>
        <Button label="Cancel" variant="quaternary" onClick={closeDrawer} />
        <Observer>{() => <Button label="Save" onClick={onSave} disabled={!formState.valid} />}</Observer>
      </div>
    </>
  );
}

type FormValue = SaveItemInput & { materialLeadTimeConfig: MaterialLeadTimeFormValue };

const formConfig: ObjectConfig<FormValue> = {
  materialLeadTimeConfig: { type: "object", config: materialLeadTimeConfigConfig },
};
